<script setup lang="ts">
import { ref, computed, watch, toRef } from 'vue';
import BaseInput from '../ui/inputs/BaseInput.vue';
import Select from '../ui/select/Select.vue';
import Price from '../ui/inputs/Price.vue';

import type { ClaimExpenseInput, ExpenseInput, RetrieveClaimWithRelationsResponse } from '@/interfaces';
import FileInput from '../files/FileInput.vue';
import { CurrencyEuroIcon, ReceiptRefundIcon } from '@heroicons/vue/20/solid';
import { TrainFront, BedDouble, CarTaxiFront, PlaneTakeoff, PlaneLanding, UtensilsCrossed, PhoneOutgoing, TentTree, Receipt } from "lucide-vue-next";
import BaseButton from '@/components/ui/buttons/BaseButton.vue';
import adminAPI from '@/services/api';
import { useNotificationStore } from '@/store';
import { LoadedFile } from '../files/types';
import { ExpenseTypesMap } from '@/utils/config/constants';

const emits = defineEmits(["expense-created"]);

const props = defineProps<{
  claimId: string;
  usersClaims: RetrieveClaimWithRelationsResponse["usersClaims"];
  isB2B: boolean;
}>();

const paidByOptions = computed<Array<{ value: ClaimExpenseInput["paidBy"] | string; label: string; }>>(() => [
  { value: "TRAVEL_AGENCY", label: "Perte agence" },
  { value: "BUSINESS_TRAVEL_COMPANY", label: "Facturé sur le compte de l'entreprise", disabled: !props.isB2B },
  ...props.usersClaims.map(userClaim => ({
    value: userClaim.id, label: `Payé par ${userClaim.user.firstName} ${userClaim.user.lastName.toUpperCase()} (Passager)`
  }))
]);

const expenseTypeOptions = Object.values(ExpenseTypesMap);

const lostPrestationOptions: Array<{ value: boolean; label: string; }> = [
  { value: true, label: "Prestation perdue" },
  { value: false, label: "Nouvelle dépense" },
];

const defaultExpense: ClaimExpenseInput = {
  paidBy: "TRAVEL_AGENCY",
  expense: {
    expenseType: "RETURN_FLIGHT",
    price: { amount: 0, currency: "EUR", precision: 2 },
    isLostPrestation: false,
    expenseName: "",
    file: undefined,
  }
};
const claimExpense = ref<ClaimExpenseInput>(defaultExpense);
function getBody() {
  const loadedFile = claimExpense.value.expense.file?.[0] as any as LoadedFile;
  const body = { ...claimExpense.value };
  if (loadedFile) {
    body.expense.file = {
      name: loadedFile.file.name,
      mimeType: loadedFile.file.type,
      document: loadedFile.content,
    };
  }
  return body;
}

const isLoading = ref(false);
async function addExpense() {
  isLoading.value = true;
  const { success } = await adminAPI.addClaimExpense(props.claimId, getBody());
  isLoading.value = false;
  if (success) {
    emits("expense-created");
    useNotificationStore().emitNotification({ title: "Dépense ajoutée avec succès" });
    claimExpense.value = defaultExpense;
  }
}
</script>
<template>
<div class="p-6 m-2 border rounded-lg shadow">
  <form class="text-left">
    <div class="grid grid-cols-2 gap-4">
      <BaseInput label="Nom de la dépense" placeholder="Avion retour 26/05" v-model="claimExpense.expense.expenseName" />
      <Price label="Montant" placeholder="128.52" :icon="ReceiptRefundIcon" v-model="claimExpense.expense.price" />
      <Select
        @update:modelValue="selected => claimExpense.expense.expenseType = selected.value"
        label="Type de dépense" :options="expenseTypeOptions" :selected-option="4"
        select-check-position="right"
      />
      <Select
        @update:modelValue="selected => claimExpense.expense.isLostPrestation = selected.value"
        label="Perte ou nouvelle dépense ?"
        :options="lostPrestationOptions" :selected-option="1"
      />
    </div>
    <div class="my-6">
      <Select
        @update:modelValue="selected => claimExpense.paidBy = selected.value"
        label="Qui a payé ?"
        description="Nous ferons indemniser le payeur"
        :options="paidByOptions" :selected-option="0"
      />
      <div class="mt-4">
        <p class="text-sm font-medium leading-6 text-gray-900">Facture</p>
        <p class="text-sm text-slate-600">Merci d'indiquer 1 justificatif par dépense</p>
        <FileInput @files-updated="(files) => claimExpense.expense.file = files as any" />
      </div>
    </div>
    <div class="flex flex-row-reverse">
      <BaseButton text="Enregistrer le préjudice" size="lg" @click="addExpense()" :icon="CurrencyEuroIcon" :loading="isLoading" />
    </div>
  </form>
</div>
</template>